import { useEffect, useState, useCallback } from 'react';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import CircularGauge from '@/components/base/CircularGauge';
import type { InverterTelemetryResponse } from '@/pages/assets/detail/components/inverterTelemetryTypes';
import { inverterTelemetryMock } from '@/mocks/inverterTelemetry';
import type { BessResponse } from '@/mocks/bessTelemetry';
import { useTheme } from '@/hooks/useTheme';

interface Props {
  assetId: string;
  assetName: string;
}

const REFRESH_MS = 5 * 60 * 1000;

function fmtKw(v: number | null): string {
  if (v == null) return '—';
  return v.toFixed(1);
}

function fmtPct(v: number | null): string {
  if (v == null) return '—';
  return `${v.toFixed(1)}%`;
}

function fmtDelta(v: number | null, pct: number | null): string {
  if (v == null || pct == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)} kW · ${sign}${pct.toFixed(1)}%`;
}

function fmtPowerFlow(flow: string): string {
  const map: Record<string, string> = {
    discharging: 'Discharge / Output',
    'discharging_or_output_active': 'Discharge / Output Active',
    charging: 'Charge / Input',
    'charging_or_input_active': 'Charge / Input Active',
    idle: 'Idle',
    standby: 'Standby',
  };
  const key = flow.toLowerCase().trim();
  return map[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function HomeTelemetryWidget({ assetId, assetName }: Props) {
  const [data, setData] = useState<InverterTelemetryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const [bessData, setBessData] = useState<BessResponse | null>(null);

  const { isDark } = useTheme();
  const isTabaki = assetName?.includes('Табаки') || assetName?.includes('Tabaki') || assetId === '12a78662-a28d-40d5-9eb7-e83ebb158b41';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<InverterTelemetryResponse>(
        `/forecasting/assets/${assetId}/inverters/latest`
      );
      setData(result);
      setLastRefresh(new Date());
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setData(inverterTelemetryMock);
        setLastRefresh(new Date());
      } else {
        setError(getApiErrorMessage(err, 'Failed to load telemetry'));
      }
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  const fetchBess = useCallback(async () => {
    if (!isTabaki) {
      setBessData(null);
      return;
    }
    try {
      const result = await api.get<BessResponse>(
        `/forecasting/assets/${assetId}/bess/latest`
      );
      setBessData(result);
    } catch (err) {
      // audit 2026-07-27: no fabricated fallback — null hides BESS panel instead of fake numbers
      setBessData(null);
    }
  }, [assetId, isTabaki]);

  useEffect(() => {
    fetchData();
    fetchBess();
    const timer = setInterval(() => {
      fetchData();
      fetchBess();
    }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchData, fetchBess]);

  const totalPacKw = data?.totalPacKw ?? data?.totals?.totalPacKw ?? null;
  const pctOfNominal = data?.percentOfNominal ?? data?.totals?.percentOfNominal ?? null;
  const plantNominalKw = data?.nominal?.plantKw ?? 1000;
  const forecast = data?.forecast;
  const comparison = data?.comparison;

  const deltaColor =
    comparison?.deltaKw == null
      ? '#6B6B70'
      : comparison.deltaKw < 0
        ? 'var(--accent)'
        : '#0D7A3C';

  const availableInverters = data?.availableInverterCount ?? '—';
  const expectedInverters = data?.expectedInverterCount ?? '—';
  const qualityFlag = data?.qualityFlag ?? '—';
  const qualityColor = qualityFlag === 'ok' ? '#0D7A3C' : 'var(--accent)';
  const readAtLocal = data?.readAtLocal ?? '—';

  // BESS derived (only meaningful when isTabaki)
  const socPct = isTabaki ? (bessData?.gauges?.socPct ?? null) : null;
  const powerFlow = isTabaki ? (bessData?.pcs?.powerFlow ?? '—') : '—';
  const powerFlowLabel = isTabaki ? fmtPowerFlow(powerFlow) : '—';
  const operationalStatus = isTabaki ? (bessData?.status?.operationalStatus ?? '—') : '—';
  const socColor = socPct != null
    ? socPct >= 80 ? '#0D7A3C' : socPct >= 40 ? '#D97706' : 'var(--accent)'
    : '#9B9BA4';
  const bessStatusColor = isTabaki
    ? (powerFlow.includes('discharging') || powerFlow.includes('charging')
        ? '#0D7A3C'
        : powerFlow === 'idle' || powerFlow === 'standby'
          ? '#D97706'
          : 'var(--accent)')
    : '#9B9BA4';

  // Theme colors
  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const textDim = isDark ? '#4A4A50' : '#9B9BA4';
  const gaugeTrack = isDark ? 'rgba(255,255,255,0.06)' : '#F0F0F2';
  const btnBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-2)';
  const forecastIconBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(31,91,143,0.08)';
  const noDataBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const noDataColor = isDark ? '#4A4A50' : '#C0C0C6';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
      {/* Left — large gauge "Live Modbus Readings" */}
      <div
        className="xl:col-span-1 rounded-xl p-5 flex flex-col items-center justify-center"
        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
      >
        {/* Header */}
        <div className="w-full flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: iconBg }}
            >
              <i className="ri-flashlight-line text-sm" style={{ color: 'var(--accent)' }}></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ color: textMain }}>
                Live Modbus Readings
              </h3>
              <p className="text-[11px]" style={{ color: textMuted }}>
                30 min interval · Inverter telemetry
              </p>
            </div>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer transition-colors"
            style={{ background: btnBg, color: textMuted }}
            title="Refresh now"
          >
            <i
              className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} text-xs`}
            ></i>
          </button>
        </div>

        {/* Error state */}
        {error && (
          <div className="text-center py-6">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2"
              style={{ background: noDataBg }}
            >
              <i className="ri-error-warning-line text-lg" style={{ color: 'var(--accent)' }}></i>
            </div>
            <p className="text-xs font-medium" style={{ color: textMain }}>
              Failed to load telemetry
            </p>
            <button
              onClick={fetchData}
              className="mt-2 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap"
              style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && !data && (
          <div className="text-center py-8">
            <i className="ri-loader-4-line animate-spin text-xl" style={{ color: textMuted }}></i>
            <p className="text-xs mt-2" style={{ color: textMuted }}>
              Loading telemetry...
            </p>
          </div>
        )}

        {/* Gauge */}
        {!error && data && (
          <>
            <CircularGauge
              value={totalPacKw}
              max={plantNominalKw}
              size={170}
              strokeWidth={9}
              color="#0D7A3C"
              trackColor={gaugeTrack}
              muted={totalPacKw == null}
            >
              <span
                className="text-2xl font-bold leading-tight"
                style={{ color: totalPacKw == null ? '#9B9BA4' : textMain }}
              >
                {fmtKw(totalPacKw)}
              </span>
              <span className="text-xs mt-1" style={{ color: textMuted }}>
                kW
              </span>
              <span className="text-xs mt-1 font-medium" style={{ color: textMuted }}>
                {fmtPct(pctOfNominal)}
              </span>
            </CircularGauge>
            <div className="mt-3 text-sm font-semibold" style={{ color: textMain }}>
              Total AC Power
            </div>
            {data.readAtLocal && (
              <div className="text-[11px] mt-1" style={{ color: textDim }}>
                Updated {data.readAtLocal}
              </div>
            )}
          </>
        )}
      </div>

      {/* Right — 2 rows × 3 columns tile grid */}
      <div className="xl:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Tile 1: Total AC Power */}
        <div
          className="rounded-xl p-4 flex flex-col justify-between"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: iconBg }}
            >
              <i className="ri-flashlight-line text-sm" style={{ color: 'var(--accent)' }}></i>
            </div>
            <span className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap" style={{ color: textMuted }}>
              Total AC Power
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-2xl font-bold tabular-nums leading-none" style={{ color: textMain }}>
              {fmtKw(totalPacKw)}
            </span>
            <span className="text-sm font-medium" style={{ color: textMuted }}>kW</span>
          </div>
          <div className="text-xs mt-2 font-medium" style={{ color: textMuted }}>
            {fmtPct(pctOfNominal)} of nominal
          </div>
        </div>

        {/* Tile 2: Forecast Current */}
        <div
          className="rounded-xl p-4 flex flex-col justify-between"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: forecastIconBg }}
            >
              <i className="ri-bar-chart-2-line text-sm" style={{ color: '#1F5B8F' }}></i>
            </div>
            <span className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap" style={{ color: textMuted }}>
              Forecast Current
            </span>
          </div>
          <div className="flex items-baseline gap-1.5">
            {forecast?.forecastCurrentKw != null ? (
              <>
                <span className="text-2xl font-bold tabular-nums leading-none" style={{ color: textMain }}>
                  {fmtKw(forecast.forecastCurrentKw)}
                </span>
                <span className="text-sm font-medium" style={{ color: textMuted }}>kW</span>
              </>
            ) : (
              <span className="text-sm font-medium" style={{ color: textDim }}>No forecast</span>
            )}
          </div>
          {forecast?.forecastForLocal ? (
            <div className="text-xs mt-2" style={{ color: textMuted }}>
              for {forecast.forecastForLocal}
            </div>
          ) : (
            <div className="text-xs mt-2" style={{ color: textDim }}>—</div>
          )}
        </div>

        {/* Tile 3: SOC — BESS (only for Табаки) */}
        <div
          className="rounded-xl p-4 flex flex-col justify-between"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${socColor}15` }}
            >
              <i className="ri-battery-2-charge-line text-sm" style={{ color: socColor }}></i>
            </div>
            <span className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap" style={{ color: textMuted }}>
              SOC
            </span>
          </div>
          {isTabaki ? (
            <>
              <div className="flex items-baseline gap-1.5">
                {socPct != null ? (
                  <>
                    <span className="text-2xl font-bold tabular-nums leading-none" style={{ color: socColor }}>
                      {socPct.toFixed(1)}
                    </span>
                    <span className="text-sm font-medium" style={{ color: textMuted }}>%</span>
                  </>
                ) : (
                  <span className="text-sm font-medium" style={{ color: textDim }}>No data</span>
                )}
              </div>
              {socPct != null && (
                <div className="mt-2 w-full h-1.5 rounded-full" style={{ background: gaugeTrack }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(100, Math.max(0, socPct))}%`,
                      background: socColor,
                    }}
                  ></div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 gap-1">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: noDataBg }}>
                <i className="ri-forbid-line text-lg" style={{ color: noDataColor }}></i>
              </div>
              <span className="text-xs font-medium" style={{ color: textDim }}>No Data</span>
            </div>
          )}
          <div className="text-xs mt-2 font-medium" style={{ color: textMuted }}>
            Рівень заряду (SOC)
          </div>
        </div>

        {/* Tile 4: Delta vs Forecast */}
        <div
          className="rounded-xl p-4 flex flex-col justify-between"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${deltaColor}15` }}
            >
              <i
                className={
                  comparison?.deltaKw == null
                    ? 'ri-subtract-line'
                    : comparison.deltaKw >= 0
                      ? 'ri-arrow-up-line'
                      : 'ri-arrow-down-line'
                }
                style={{ color: deltaColor, fontSize: '14px' }}
              ></i>
            </div>
            <span className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap" style={{ color: textMuted }}>
              Delta vs Forecast
            </span>
          </div>
          <div className="text-xl font-bold tabular-nums leading-none" style={{ color: deltaColor }}>
            {fmtDelta(comparison?.deltaKw, comparison?.deltaPct)}
          </div>
          <div className="text-xs mt-2" style={{ color: textDim }}>
            Actual vs predicted
          </div>
        </div>

        {/* Tile 5: Inverters · Quality · Update */}
        <div
          className="rounded-xl p-4 flex flex-col justify-between"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(13,122,60,0.08)' }}
            >
              <i className="ri-cpu-line text-sm" style={{ color: '#0D7A3C' }}></i>
            </div>
            <span className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap" style={{ color: textMuted }}>
              Inverters
            </span>
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[10px] font-bold tracking-wider uppercase" style={{ color: textDim }}>Inverters:</span>
              <span className="font-semibold tabular-nums text-xs" style={{ color: textMain }}>
                {availableInverters}
                <span style={{ color: textDim }}> / {expectedInverters}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[10px] font-bold tracking-wider uppercase" style={{ color: textDim }}>Quality:</span>
              <span className="font-semibold capitalize text-xs" style={{ color: qualityColor }}>
                {qualityFlag}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-[10px] font-bold tracking-wider uppercase" style={{ color: textDim }}>Update:</span>
              <span className="font-mono text-[11px] whitespace-nowrap" style={{ color: textMuted }}>
                {readAtLocal}
              </span>
            </div>
          </div>
        </div>

        {/* Tile 6: BESS STATUS (only for Табаки) */}
        <div
          className="rounded-xl p-4 flex flex-col justify-between"
          style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `${bessStatusColor}15` }}
            >
              <i className="ri-battery-charge-line text-sm" style={{ color: bessStatusColor }}></i>
            </div>
            <span className="text-[10px] font-bold tracking-widest uppercase whitespace-nowrap" style={{ color: textMuted }}>
              BESS STATUS
            </span>
          </div>
          {isTabaki ? (
            <>
              <div className="flex items-baseline gap-1.5">
                <span className="text-xl font-bold tabular-nums leading-none" style={{ color: bessStatusColor }}>
                  {powerFlowLabel}
                </span>
              </div>
              <div className="text-xs mt-2" style={{ color: textMuted }}>
                Operational: <span className="font-semibold capitalize" style={{ color: operationalStatus === 'normal' ? '#0D7A3C' : operationalStatus === 'degraded' ? '#D97706' : 'var(--accent)' }}>{operationalStatus}</span>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center flex-1 gap-1">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: noDataBg }}>
                <i className="ri-forbid-line text-lg" style={{ color: noDataColor }}></i>
              </div>
              <span className="text-xs font-medium" style={{ color: textDim }}>No Data</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}